// lib/constants.ts - Medical App Constants

// ==================================================
// DOSAGE OPTIONS
// ==================================================
export const DOSAGE_OPTIONS = [
  // Standard dosing patterns
  "1-0-0",      // Once daily (morning)
  "0-1-0",      // Once daily (afternoon)
  "0-0-1",      // Once daily (evening/night)
  "1-0-1",      // Twice daily
  "1-1-0",      // Twice daily (morning + afternoon)
  "0-1-1",      // Twice daily (afternoon + evening)
  "1-1-1",      // Three times daily
  "2-0-0",      // Two tablets morning
  "1-1-2",      // Variable dosing
  "2-2-2",      // High dose TID
  
  // Special dosing
  "1 tab",      // Simple single dose
  "2 tabs",     // Two tablets
  "SOS",        // As needed
  "1 puff",     // For inhalers
  "2 puffs",    // For inhalers
  "5ml",        // For syrups
  "10ml",       // For syrups
  "Apply",      // For topical
  
  // Injection dosing
  "1 amp",      // One ampoule
  "1 vial",     // One vial
  "As directed" // Doctor's discretion
];

// ==================================================
// WHEN TO TAKE OPTIONS
// ==================================================
export const WHEN_OPTIONS = [
  // Meal timing
  "After Food",
  "Before Food",
  "Empty Stomach",
  "SOS",
  "With Food",

  // Time-based
  "Morning",
  "Afternoon", 
  "Evening",
  "Bedtime",
  "Before Sleep",
  
  // Special timing
  "As needed",
  "PRN",
  "30 min before food",
  "1 hour after food",
  
  // Specific instructions
  "On alternate days",
  "Weekly",
  "Twice weekly",
  "Every 8 hours",
  "Every 12 hours"
];

// ==================================================
// DURATION OPTIONS
// ==================================================
export const DURATION_OPTIONS = [
  // Short term
  "3 days",
  "5 days", 
  "7 days",
  "10 days",
  "14 days",
  "1 week",
  "2 weeks",
  
  // Medium term
  "1 month",
  "2 months", 
  "3 months",
  "6 months",
  
  // Long term
  "Continue",
  "Till review",
  "As directed",
  "Long term",
  "Chronic"
];

// ==================================================
// COMPLAINT SEVERITY OPTIONS
// ==================================================
export const SEVERITY_OPTIONS = [
  "Mild",
  "Moderate", 
  "Severe",
  "Very severe"
];

// ==================================================
// COMPLAINT FREQUENCY OPTIONS  
// ==================================================
export const FREQUENCY_OPTIONS = [
  "Continuous",
  "Intermittent",
  "Multiple times daily",
  "3-4 times daily", 
  "Twice daily",
  "Once daily",
  "Occasionally",
  "At night only",
  "In morning",
  "After meals",
  "Before meals",
  "On exertion",
  "At rest"
];

// ==================================================
// COMPLAINT DURATION OPTIONS
// ==================================================
export const DURATION_OPTIONS_COMPLAINTS = [
  "Since morning",
  "Since yesterday", 
  "Since last week",
  "1 day",
  "2 days",
  "3 days", 
  "1 week",
  "2 weeks",
  "1 month",
  "2 months",
  "3 months",
  "6 months",
  "1 year",
  "For few days",
  "For 1 week",
  "About 1 week",
  "About 2 weeks",
  "Chronic",
  "Long standing"
];

// ==================================================
// MEDICAL SPECIALTIES
// ==================================================
export const MEDICAL_SPECIALTIES = [
  "General Medicine",
  "Cardiology", 
  "Endocrinology",
  "Gastroenterology",
  "Pulmonology",
  "Nephrology",
  "Neurology",
  "Psychiatry",
  "Dermatology",
  "Orthopedics",
  "Ophthalmology",
  "ENT",
  "Gynecology",
  "Pediatrics",
  "Surgery"
];

// ==================================================
// VITAL SIGNS NORMAL RANGES
// ==================================================
export const VITAL_RANGES = {
  BP_SYSTOLIC: { min: 90, max: 140, normal: "90-140" },
  BP_DIASTOLIC: { min: 60, max: 90, normal: "60-90" },
  PULSE: { min: 60, max: 100, normal: "60-100" },
  TEMPERATURE: { min: 96.8, max: 99.5, normal: "96.8-99.5" },
  SPO2: { min: 95, max: 100, normal: "95-100" },
  WEIGHT: { min: 30, max: 200, normal: "Variable" }
};

// ==================================================
// COMMON DIAGNOSES
// ==================================================
export const COMMON_DIAGNOSES = [
  // Infectious Diseases
  "Upper Respiratory Tract Infection",
  "Lower Respiratory Tract Infection", 
  "Gastroenteritis",
  "Urinary Tract Infection",
  "Viral Fever",
  "Dengue Fever",
  "Malaria",
  "Typhoid Fever",
  
  // Chronic Conditions
  "Type 2 Diabetes Mellitus",
  "Type 1 Diabetes Mellitus", 
  "Essential Hypertension",
  "Ischemic Heart Disease",
  "Chronic Kidney Disease",
  "Hypothyroidism",
  "Hyperthyroidism",
  "Asthma",
  "COPD",
  
  // Gastrointestinal
  "Gastroesophageal Reflux Disease",
  "Peptic Ulcer Disease",
  "Irritable Bowel Syndrome",
  "Inflammatory Bowel Disease",
  "Hepatitis",
  "Cholecystitis",
  
  // Musculoskeletal
  "Osteoarthritis",
  "Rheumatoid Arthritis",
  "Low Back Pain",
  "Cervical Spondylosis",
  "Fibromyalgia",
  
  // Mental Health
  "Depression",
  "Anxiety Disorder",
  "Bipolar Disorder",
  "Schizophrenia",
  "Sleep Disorder",
  
  // Others
  "Migraine",
  "Tension Headache",
  "Allergic Rhinitis",
  "Eczema",
  "Psoriasis"
];

// ==================================================
// INVESTIGATION CATEGORIES
// ==================================================
export const INVESTIGATION_CATEGORIES = {
  BLOOD_TESTS: [
    "CBC", "CBC with ESR", "ESR", "Hemoglobin", "Platelet Count"
  ],
  SUGAR_TESTS: [
    "FBS", "PPBS", "RBSL", "HbA1c", "Random Blood Sugar"
  ],
  LIVER_FUNCTION: [
    "LFT", "SGPT", "SGOT", "Bilirubin Total", "Alkaline Phosphatase"
  ],
  KIDNEY_FUNCTION: [
    "KFT", "CREATININE", "Urea", "BUN", "Uric Acid"
  ],
  CARDIAC: [
    "ECG", "2D Echo", "Troponin I", "CK-MB", "Pro BNP"
  ],
  IMAGING: [
    "Chest X-Ray", "CXR PA VIEW", "USG ABDO PELVIS", "CT BRAIN P"
  ]
};

// ==================================================
// FORM VALIDATION RULES
// ==================================================
export const VALIDATION_RULES = {
  REQUIRED_FIELDS: ["patient_id", "date"],
  MIN_CONTENT_REQUIRED: 1, // At least one complaint, medicine, or diagnosis
  MAX_COMPLAINTS: 10,
  MAX_MEDICINES: 15,
  MAX_INVESTIGATIONS: 20
};

// ==================================================
// UI CONSTANTS
// ==================================================
export const UI_CONSTANTS = {
  MAX_AUTOCOMPLETE_SUGGESTIONS: 8,
  DEBOUNCE_DELAY: 300,
  ANIMATION_DURATION: 200,
  MAX_RECENT_ITEMS: 10
};

// ==================================================
// DATE/TIME FORMATS
// ==================================================
export const DATE_FORMATS = {
  DISPLAY: "MMM dd, yyyy",
  INPUT: "yyyy-MM-dd", 
  API: "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'",
  TIME_DISPLAY: "h:mm a"
};

// ==================================================
// APPOINTMENT STATUS
// ==================================================
export const APPOINTMENT_STATUS = {
  SCHEDULED: "scheduled",
  COMPLETED: "completed", 
  CANCELLED: "cancelled",
  NO_SHOW: "no_show",
  RESCHEDULED: "rescheduled"
};

// ==================================================
// PRESCRIPTION STATUS
// ==================================================
export const PRESCRIPTION_STATUS = {
  ACTIVE: "active",
  COMPLETED: "completed",
  DISCONTINUED: "discontinued",
  ON_HOLD: "on_hold"
};